import type { QueryClient } from "@tanstack/react-query";
import { recordPerformedActionInCaches } from "web_myplann/Api/Portfolio/alertBoxesCacheUtils";
import { PortfolioKeys } from "web_myplann/Api/Portfolio/portfolioKeys";
import {
	AlertBoxActionEnum,
	AlertBoxActionStateEnum,
	type PortfolioAlertBoxDTO,
} from "web_myplann/Infrastructure/Api/Api";
import type {
	AlertBoxActionPerformedRequest,
	BridgeAlertBoxAction,
	BridgeAlertBoxActionState,
} from "web_myplann/Utils/WebViewModel";

const bridgeActionToApi: Record<BridgeAlertBoxAction, AlertBoxActionEnum> = {
	CompareRates: AlertBoxActionEnum.CompareRates,
};

const bridgeStateToApi: Record<
	BridgeAlertBoxActionState,
	AlertBoxActionStateEnum
> = {
	Sent: AlertBoxActionStateEnum.Sent,
};

const isBridgeAlertBoxAction = (
	value: unknown,
): value is BridgeAlertBoxAction =>
	typeof value === "string" && Object.hasOwn(bridgeActionToApi, value);

const isBridgeAlertBoxActionState = (
	value: unknown,
): value is BridgeAlertBoxActionState =>
	typeof value === "string" && Object.hasOwn(bridgeStateToApi, value);

const offersAction =
	(action: AlertBoxActionEnum) => (alert: PortfolioAlertBoxDTO) =>
		alert.primaryAction === action || alert.secondaryAction === action;

export const handleAlertBoxActionPerformed = (
	payload: unknown,
	queryClient: QueryClient,
): void => {
	if (typeof payload !== "object" || payload === null) {
		return;
	}

	const { contractId, action, state } = payload as Partial<
		AlertBoxActionPerformedRequest["payload"]
	>;

	if (
		typeof contractId !== "number" ||
		!Number.isFinite(contractId) ||
		!isBridgeAlertBoxAction(action) ||
		!isBridgeAlertBoxActionState(state)
	) {
		return;
	}

	const apiAction = bridgeActionToApi[action];
	const apiState = bridgeStateToApi[state];

	recordPerformedActionInCaches(
		queryClient,
		contractId,
		offersAction(apiAction),
		apiAction,
		apiState,
	);

	void queryClient.invalidateQueries({
		queryKey: PortfolioKeys.alertBoxesAll(contractId),
	});
};
